import {
  clamp,
  distance,
  distanceSquared,
  dot,
  subtract,
  type Vec2,
} from "./math";
import type { Aabb } from "./spatial-hash";

export const circleAabb = (center: Vec2, radius: number): Aabb => ({
  minX: center.x - radius,
  minY: center.y - radius,
  maxX: center.x + radius,
  maxY: center.y + radius,
});

export const capsuleAabb = (start: Vec2, end: Vec2, radius: number): Aabb => ({
  minX: Math.min(start.x, end.x) - radius,
  minY: Math.min(start.y, end.y) - radius,
  maxX: Math.max(start.x, end.x) + radius,
  maxY: Math.max(start.y, end.y) + radius,
});

export const pointSegmentDistanceSquared = (
  point: Vec2,
  start: Vec2,
  end: Vec2,
): number => {
  const segment = subtract(end, start);
  const lengthSquared = dot(segment, segment);
  if (lengthSquared <= Number.EPSILON) return distanceSquared(point, start);
  const amount = clamp(
    dot(subtract(point, start), segment) / lengthSquared,
    0,
    1,
  );
  const closest = {
    x: start.x + segment.x * amount,
    y: start.y + segment.y * amount,
  };
  return distanceSquared(point, closest);
};

export const circleCapsuleIntersects = (
  center: Vec2,
  circleRadius: number,
  capsuleStart: Vec2,
  capsuleEnd: Vec2,
  capsuleRadius: number,
): boolean => {
  const combinedRadius = circleRadius + capsuleRadius;
  return (
    pointSegmentDistanceSquared(center, capsuleStart, capsuleEnd) <=
    combinedRadius * combinedRadius
  );
};

export const circlesIntersect = (
  firstCenter: Vec2,
  firstRadius: number,
  secondCenter: Vec2,
  secondRadius: number,
): boolean => distance(firstCenter, secondCenter) <= firstRadius + secondRadius;

export const circleHitsCircularBoundary = (
  center: Vec2,
  circleRadius: number,
  arenaRadius: number,
): boolean => distance(center, { x: 0, y: 0 }) + circleRadius > arenaRadius;

const orientation = (first: Vec2, second: Vec2, third: Vec2): number =>
  (second.x - first.x) * (third.y - first.y) -
  (second.y - first.y) * (third.x - first.x);

const between = (value: number, first: number, second: number): boolean =>
  value >= Math.min(first, second) - 1e-9 &&
  value <= Math.max(first, second) + 1e-9;

const pointOnSegment = (point: Vec2, start: Vec2, end: Vec2): boolean =>
  Math.abs(orientation(start, end, point)) <= 1e-9 &&
  between(point.x, start.x, end.x) &&
  between(point.y, start.y, end.y);

const segmentsIntersect = (
  firstStart: Vec2,
  firstEnd: Vec2,
  secondStart: Vec2,
  secondEnd: Vec2,
): boolean => {
  const firstSideStart = orientation(firstStart, firstEnd, secondStart);
  const firstSideEnd = orientation(firstStart, firstEnd, secondEnd);
  const secondSideStart = orientation(secondStart, secondEnd, firstStart);
  const secondSideEnd = orientation(secondStart, secondEnd, firstEnd);
  if (
    Math.sign(firstSideStart) !== Math.sign(firstSideEnd) &&
    Math.sign(secondSideStart) !== Math.sign(secondSideEnd)
  ) {
    return true;
  }
  return (
    pointOnSegment(secondStart, firstStart, firstEnd) ||
    pointOnSegment(secondEnd, firstStart, firstEnd) ||
    pointOnSegment(firstStart, secondStart, secondEnd) ||
    pointOnSegment(firstEnd, secondStart, secondEnd)
  );
};

export const segmentSegmentDistanceSquared = (
  firstStart: Vec2,
  firstEnd: Vec2,
  secondStart: Vec2,
  secondEnd: Vec2,
): number => {
  if (segmentsIntersect(firstStart, firstEnd, secondStart, secondEnd)) return 0;
  return Math.min(
    pointSegmentDistanceSquared(firstStart, secondStart, secondEnd),
    pointSegmentDistanceSquared(firstEnd, secondStart, secondEnd),
    pointSegmentDistanceSquared(secondStart, firstStart, firstEnd),
    pointSegmentDistanceSquared(secondEnd, firstStart, firstEnd),
  );
};

export const sweptCircleCapsuleIntersects = (
  motionStart: Vec2,
  motionEnd: Vec2,
  circleRadius: number,
  capsuleStart: Vec2,
  capsuleEnd: Vec2,
  capsuleRadius: number,
): boolean => {
  const combinedRadius = circleRadius + capsuleRadius;
  return (
    segmentSegmentDistanceSquared(
      motionStart,
      motionEnd,
      capsuleStart,
      capsuleEnd,
    ) <=
    combinedRadius * combinedRadius
  );
};

export const sweptCirclesIntersect = (
  firstStart: Vec2,
  firstEnd: Vec2,
  firstRadius: number,
  secondStart: Vec2,
  secondEnd: Vec2,
  secondRadius: number,
): boolean => {
  const relativeStart = subtract(firstStart, secondStart);
  const relativeEnd = subtract(firstEnd, secondEnd);
  const combinedRadius = firstRadius + secondRadius;
  return (
    pointSegmentDistanceSquared({ x: 0, y: 0 }, relativeStart, relativeEnd) <=
    combinedRadius * combinedRadius
  );
};

export const sweptCircleIntersectsCircle = (
  motionStart: Vec2,
  motionEnd: Vec2,
  movingRadius: number,
  center: Vec2,
  stationaryRadius: number,
): boolean => {
  const combinedRadius = movingRadius + stationaryRadius;
  return (
    pointSegmentDistanceSquared(center, motionStart, motionEnd) <=
    combinedRadius * combinedRadius
  );
};
